import React, { memo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { signAndUpload } from "./FileInput/s3-upload";
import Pica from "pica";
import {
  inputTextClasses,
  primaryButtonSmallClasses,
  thirdButtonGrayRoundClasses,
  thirdButtonRoundClasses
} from "../manager/classes";
import DeleteNoState from "../manager/DeleteNoState";
import { LoadingSpinner } from "../../../../components/common/Loading";

const THUMBNAIL_WIDTH = 150;
const RESIZE_OPTS = {
  unsharpAmount: 80,
  unsharpRadius: 0.6,
  unsharpThreshold: 2,
  alpha: true
};

const SingleFileUpload = memo(function SingleFileUpload({
  input: { value, onChange, name: fieldName },
  id,
  acl = "public-read",
  directory = "images",
  noPreview = false,
  noLabel = false,
  tabIndex = 0,
  style,
  accept,
  action,
  callbackFn = () => {},
  startCallbackFn = () => {}
}) {
  const [valueInState, setValueInState] = useState(!value ? null : value);
  const [resetConfirmed, setResetConfirmed] = useState(false);
  const [altTextEdit, setAltTextEdit] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const pica = Pica();
  let uploadInput;

  const handleUpload = async () => {
    setIsUploading(true);
    startCallbackFn();
    let output = {};
    const inputFile = uploadInput["files"][0];
    const id = uuidv4();

    // determine image dimensions and create thumbnail
    const dimensionsAndThumbnail = await new Promise((resolve, _reject) => {
      if (
        inputFile.type === "image/jpeg" ||
        inputFile.type === "image/png" ||
        inputFile.type === "image/webp" ||
        inputFile.type === "image/gif"
      ) {
        const url = URL.createObjectURL(inputFile);
        const img = new Image();
        img.onload = async () => {
          const offScreenCanvas = document.createElement("canvas");
          offScreenCanvas.width = THUMBNAIL_WIDTH;
          offScreenCanvas.height = (THUMBNAIL_WIDTH / img.width) * img.height;

          const thumbnailCanvas = await pica.resize(
            img,
            offScreenCanvas,
            RESIZE_OPTS
          );
          const thumbnailBlob = await pica.toBlob(thumbnailCanvas, "image/png");

          resolve({
            width: img.width,
            height: img.height,
            thumbnail: new File([thumbnailBlob], "thumbnail.png"),
            thumbWidth: 150,
            thumbHeight: offScreenCanvas.height
          });
        };
        img.src = url;
      } else {
        resolve({ height: null, width: null, thumbnail: null });
      }
    });

    // sign and upload main file
    const file = await signAndUpload(inputFile, {
      id,
      name: inputFile.name,
      mimetype: inputFile.type,
      size: inputFile.size,
      height: dimensionsAndThumbnail.height,
      width: dimensionsAndThumbnail.width,
      acl,
      directory
    });

    // sign and upload thumbnail if applicable
    if (dimensionsAndThumbnail.thumbnail) {
      const thumbnail = await signAndUpload(dimensionsAndThumbnail.thumbnail, {
        id,
        name: "thumbnail.png",
        mimetype: "image/png",
        size: dimensionsAndThumbnail.size,
        height: dimensionsAndThumbnail.thumbHeight,
        width: dimensionsAndThumbnail.thumbWidth,
        acl,
        directory
      });

      output = { ...file, thumbnail };
    } else {
      output = file;
    }

    setValueInState(output);
    onChange(output);
    callbackFn(file);
    setIsUploading(false);
  };

  return (
    <>
      {valueInState && valueInState?.thumbnail && noPreview === false && (
        <img src={`/secure/file?url=${valueInState["thumbnail"]["url"]}`} />
      )}
      {isUploading && (
        <div className="pb-2">
          <LoadingSpinner />
        </div>
      )}
      <div className="relative">
        {valueInState === null && (
          <div className="flex text-sm text-gray-600">
            <label
              tabIndex={tabIndex}
              htmlFor={id || fieldName}
              className={primaryButtonSmallClasses}
              style={style}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  uploadInput.click();
                }
              }}
            >
              {action && <span>{action}</span>}
              <input
                id={id || fieldName}
                name={fieldName}
                type="file"
                className="sr-only"
                onChange={handleUpload}
                ref={(ref) => {
                  uploadInput = ref;
                }}
                accept={accept}
              />
            </label>
          </div>
        )}
        {valueInState && valueInState?.name && (
          <>
            <div className="text-base text-gray-900 my-2">
              {noLabel === false && (
                <div>
                  {valueInState?.alt ? valueInState.alt : valueInState.name}
                </div>
              )}
              {noPreview === false && (
                <>
                  {!altTextEdit && (
                    <DeleteNoState
                      open={resetConfirmed}
                      setOpen={setResetConfirmed}
                      action={() => {
                        setValueInState(null);
                        onChange(null);
                        setResetConfirmed(false);
                      }}
                      message={`Are you sure you want to delete the file: ${valueInState.name}?`}
                      verb="delete"
                    >
                      <div className="flex gap-x-2 mt-2">
                        {altTextEdit === false &&
                          (valueInState?.mimetype === "image/jpeg" ||
                            valueInState?.mimetype === "image/png" ||
                            valueInState?.mimetype === "image/webp" ||
                            valueInState?.mimetype === "image/gif") && (
                            <a
                              className={thirdButtonRoundClasses}
                              onClick={() => setAltTextEdit(true)}
                            >
                              {valueInState?.alt ? "Edit" : "Set"} Description
                            </a>
                          )}
                        <a
                          className={thirdButtonRoundClasses}
                          onClick={() => setResetConfirmed(true)}
                        >
                          Reset Upload
                        </a>
                      </div>
                    </DeleteNoState>
                  )}
                </>
              )}
            </div>

            {altTextEdit && (
              <div className="flex gap-x-2 mb-2 pr-4">
                <input
                  type="text"
                  className={inputTextClasses}
                  placeholder="Description"
                  defaultValue={valueInState?.alt}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onChange(valueInState);
                      setAltTextEdit(false);
                    }
                  }}
                  onChange={(e) => {
                    setValueInState({
                      ...valueInState,
                      alt: e.target.value
                    });
                  }}
                />
                <div
                  className={thirdButtonGrayRoundClasses}
                  onClick={() => {
                    onChange(valueInState);
                    setAltTextEdit(false);
                  }}
                >
                  Done
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
});

export default SingleFileUpload;
